import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";

/**
 * The dashboard page's UI state. The dashboards themselves, their widgets,
 * charts, previews and shares are server data, read and written through the
 * shared cache (`queries/dashboards.ts`, `hooks/use-dashboards.ts`,
 * `hooks/use-dashboard-writers.ts`, NG-2811).
 */
export class DashboardStore {
  rootStore: RootStore;

  // Capacity PR3 — W4 drill-down state. ``expandedUserId`` is keyed by widget
  // because each W3 instance has its own expansion.
  expandedUserByWidget = new Map<string, string | null>();

  // Per-widget "All / over limit" capacity scope OVERRIDE (June 12 daily).
  // Only holds a value once the user toggles a widget; absent means "use the
  // widget's config default" (the BE resolves that), so the chart read sends
  // the ``capacity_scope`` param only for widgets the user explicitly toggled.
  capacityScopeByWidget = new Map<string, "all" | "over_limit">();

  // #1082: the user whose detail side panel is open, set when an owner/lead
  // name is clicked in a table widget. Null = panel closed. One panel serves
  // the whole dashboard, so this is page-scoped, not per-widget.
  panelMemberId: string | null = null;

  /** The widget whose config editor panel is open (detail page). Null = closed. */
  selectedWidgetId: string | null = null;

  /** Whether the editor should put the caret in the Name field when it opens.
   *  Set with the selection rather than beside it: the panel reads it on the
   *  render where `open` flips, and its focus manager acts once, so a signal
   *  arriving a render later is a signal that arrives too late. */
  focusNameOnOpen = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** Set the per-widget capacity scope override. The widget's chart is keyed
   *  by it, so the chart for the new scope is read (or shown from the cache). */
  setCapacityScope(widgetId: string, scope: "all" | "over_limit"): void {
    runInAction(() => {
      this.capacityScopeByWidget.set(widgetId, scope);
    });
  }

  /** Capacity PR3 — toggle the expanded user under a W3 widget. */
  toggleExpandedUser(widgetId: string, userId: string): void {
    runInAction(() => {
      const current = this.expandedUserByWidget.get(widgetId) ?? null;
      this.expandedUserByWidget.set(widgetId, current === userId ? null : userId);
    });
  }

  /** Issue 1082: open the member detail panel for an owner/lead clicked in a table. */
  openMemberPanel(userId: string): void {
    runInAction(() => {
      this.panelMemberId = userId;
    });
  }

  /** Issue 1082: close the member detail panel. */
  closeMemberPanel(): void {
    runInAction(() => {
      this.panelMemberId = null;
    });
  }

  selectWidget(widgetId: string, options?: { focusName?: boolean }): void {
    runInAction(() => {
      this.selectedWidgetId = widgetId;
      this.focusNameOnOpen = options?.focusName ?? false;
    });
  }

  clearSelectedWidget(): void {
    runInAction(() => {
      this.selectedWidgetId = null;
      this.focusNameOnOpen = false;
    });
  }
}
